import { Observable, Observer, Subject } from 'rxjs/index';
import userPool from '../userPool';
import { AuthenticationDetails, CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import Role from '../../../domain/Role';
import { history } from '../../core/createStore';

export interface UserSessionDetails {
    token: string;
    customerId: string;
    role: Role;
}

class CognitoUserAuthenticator {
    private initialUser: UserSessionDetails = { token: '', customerId: '', role: Role.Null };
    private user: UserSessionDetails = this.initialUser;
    private cognitoUser: CognitoUser;

    public getLocalUser = (): Observable<UserSessionDetails> =>
        new Observable<UserSessionDetails>(
            (observer: Observer<UserSessionDetails>): void => {
                const cognitoUser = userPool().getCurrentUser();
                if (cognitoUser != null) {
                    cognitoUser.getSession((err: string, session: CognitoUserSession) => {
                        if (err) {
                            return;
                        }
                        const user = this.parseSession(session);
                        observer.next(user);
                        this.user = user;
                    });
                }
            }
        );

    public authenticateUser = (login: string, password: string): Observable<UserSessionDetails> => {
        const subject: Subject<UserSessionDetails> = new Subject<UserSessionDetails>();
        const authDetails: AuthenticationDetails = new AuthenticationDetails({
            Username: login,
            Password: password
        });
        this.cognitoUser = new CognitoUser({
            Username: login,
            Pool: userPool()
        });
        this.cognitoUser.authenticateUser(authDetails, {
            onSuccess: this.onSuccess(subject),
            onFailure: this.onFailure(subject),
            newPasswordRequired(): void {
                history.push('/change-password');
            }
        });
        return subject;
    };

    public changePassword = (password: string): Observable<UserSessionDetails> => {
        const subject: Subject<UserSessionDetails> = new Subject<UserSessionDetails>();
        this.cognitoUser.completeNewPasswordChallenge(
            password,
            {},
            {
                onSuccess: this.onSuccess(subject),
                onFailure: this.onFailure(subject)
            }
        );
        return subject;
    };

    public get currentUserValue(): UserSessionDetails {
        return this.user;
    }

    public logout(): Observable<void> {
        return new Observable<void>(
            (observer: Observer<void>): void => {
                if (userPool().getCurrentUser() !== null) {
                    userPool()
                        .getCurrentUser()!
                        .signOut();
                    observer.next();
                }
            }
        );
    }

    // tslint:disable-next-line:typedef
    private onSuccess = (subject: Subject<UserSessionDetails>) => (result: CognitoUserSession): void => {
        this.user = this.parseSession(result);
        subject.next(this.user);
    };

    // tslint:disable-next-line:typedef
    private onFailure = (subject: Subject<UserSessionDetails>) => (e: { message: string }): void => {
        subject.error(e.message);
    };

    private getPayload = (session: CognitoUserSession, key: string): string => session.getIdToken().payload[key];

    private parseSession = (session: CognitoUserSession): UserSessionDetails => ({
        token: session.getIdToken().getJwtToken(),
        role:
            this.getPayload(session, 'cognito:groups') && this.getPayload(session, 'cognito:groups').indexOf('admin') !== -1
                ? Role.Admin
                : Role.User,
        customerId: this.getPayload(session, 'cognito:username')
    });
}

export default new CognitoUserAuthenticator();



// WEBPACK FOOTER //
// ./src/application/user/epic/CognitoUserAuthenticator.ts